import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { apiDefaultFetcher, apiGetBlobFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  BillingApiPath,
  ListBillingResponse,
  ListBillingVars,
  UsageBySiteResponse,
  UsageBySiteVars
} from '@/common/api/params/billing'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { useShowToastIfMutationError } from '@/common/app/toast'

export interface BillingSearchCondition extends SearchConditionBase {
  status: string | null
}

const billingSearchConditionParam: SearchConditionParam[] = [{ name: 'status', type: 'string' }]

export const BILLING_SEARCH_CONDITION_CLEAR: BillingSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  status: null
}

export const useBillingSearchCondition = (): [
  BillingSearchCondition,
  Dispatch<SetStateAction<BillingSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<BillingSearchCondition>(billingSearchConditionParam)
}

export const useBillingSearchApi = (condition: BillingSearchCondition) => {
  const vars: ListBillingVars = {
    page: condition.page,
    per_page: condition.perPage,
    ...(condition.status && { status: condition.status })
  }

  return useSWR<ListBillingResponse, Response, [string, ListBillingVars]>(
    [BillingApiPath.LIST_BILLING, vars],
    apiDefaultFetcher
  )
}

export const useUsageBySiteApi = (month: string | null) => {
  const vars: UsageBySiteVars = { ...(month && { month }) }
  return useSWR<UsageBySiteResponse, Response, [string, UsageBySiteVars]>(
    [BillingApiPath.USAGE_BY_SITE, vars],
    apiDefaultFetcher
  )
}

export const useDownloadUsageBySiteExcel = (onSuccess: (blob: Blob) => void) => {
  const showError = useShowToastIfMutationError()
  return useSWRMutation<Blob, Response, string, { month?: string }>(
    BillingApiPath.USAGE_BY_SITE_EXCEL,
    apiGetBlobFetcher,
    { onSuccess, onError: showError }
  )
}
